/**
 * /crm/settings/cancellations — admin toggles for which cancellation
 * sources auto-create CRM tickets.
 *
 * Each row is a single source (customer self-cancel, staff cancel,
 * payment failure, etc.). Flipping the toggle on tells the order signal
 * to open a `cancellation` ticket whenever an order with that source goes
 * to status='cancelled'; the routing matrix takes over from there.
 *
 * Separately: any cancel where online payment was already captured always
 * opens a `refund` ticket (accounts queue) — that path is not gated by
 * these toggles. See backend/docs/DROPIT_AUTO_REFUND_TICKETS.md.
 */
import { useEffect, useMemo, useState } from 'react';
import { Loader2, Save, Slash } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  useCancellationSources,
  usePatchCancellationSources,
  type CrmCancellationSource,
} from '@/api/bi/crmSettings';
import { type CrmTicketPriority } from '@/api/bi/crm';
import { apiErrorMessage } from '@/lib/apiError';
import { notify } from '@/lib/toast';
import { cn } from '@/lib/cn';

export function CrmCancellationsPage() {
  const sourcesQuery = useCancellationSources();
  const patch = usePatchCancellationSources();
  const [drafts, setDrafts] = useState<Record<string, CrmCancellationSource>>({});

  useEffect(() => {
    if (!sourcesQuery.data) return;
    const next: Record<string, CrmCancellationSource> = {};
    for (const row of sourcesQuery.data.results) {
      next[row.source] = { ...row };
    }
    setDrafts(next);
  }, [sourcesQuery.data]);

  const dirtyKeys = useMemo(() => {
    if (!sourcesQuery.data) return new Set<string>();
    const dirty = new Set<string>();
    for (const row of sourcesQuery.data.results) {
      const draft = drafts[row.source];
      if (!draft) continue;
      if (
        draft.is_auto_create !== row.is_auto_create ||
        draft.default_priority !== row.default_priority
      ) {
        dirty.add(row.source);
      }
    }
    return dirty;
  }, [drafts, sourcesQuery.data]);

  function toggle(source: string) {
    setDrafts((d) => ({ ...d, [source]: { ...d[source], is_auto_create: !d[source].is_auto_create } }));
  }

  function setPriority(source: string, priority: CrmTicketPriority) {
    setDrafts((d) => ({ ...d, [source]: { ...d[source], default_priority: priority } }));
  }

  async function saveAll() {
    if (dirtyKeys.size === 0) {
      notify.message('Nothing to save.');
      return;
    }
    const updates = Array.from(dirtyKeys).map((source) => ({
      source,
      is_auto_create: drafts[source].is_auto_create,
      default_priority: drafts[source].default_priority,
    }));
    try {
      await patch.mutateAsync(updates);
      notify.success(`Saved ${updates.length} change${updates.length === 1 ? '' : 's'}.`);
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Could not save toggles.'));
    }
  }

  const priorityOptions = sourcesQuery.data?.priorities ?? [];

  return (
    <>
      <PageHeader
        eyebrow="CRM Settings"
        title="Cancellation Sources"
        description="Choose which cancellation paths auto-open cancellation tickets. Paid online cancels always open a refund ticket as well — that is not controlled here."
        actions={
          <Button size="sm" onClick={saveAll} disabled={patch.isPending || dirtyKeys.size === 0}>
            {patch.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1 h-3.5 w-3.5" />}
            Save{dirtyKeys.size > 0 ? ` (${dirtyKeys.size})` : ''}
          </Button>
        }
      />

      {sourcesQuery.isLoading ? (
        <Skeleton className="h-64" />
      ) : sourcesQuery.isError ? (
        <ErrorState
          title="Could not load cancellation toggles"
          description={String((sourcesQuery.error as Error)?.message ?? '')}
        />
      ) : (
        <Card>
          <CardBody className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-surface text-xs uppercase tracking-[0.12em] text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left">Source</th>
                  <th className="px-4 py-2 text-left">Auto-create CRM ticket</th>
                  <th className="px-4 py-2 text-left">Default priority</th>
                </tr>
              </thead>
              <tbody>
                {Object.values(drafts).map((row) => (
                  <tr
                    key={row.source}
                    className="border-t border-border/60 transition-colors hover:bg-muted/20"
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium">{row.label}</div>
                      <code className="text-[11px] text-muted-foreground">{row.source}</code>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => toggle(row.source)}
                        className={cn(
                          'press inline-flex h-7 items-center gap-2 rounded-md border px-2.5 text-xs font-medium focus-ring',
                          row.is_auto_create
                            ? 'border-success/30 bg-success/10 text-success'
                            : 'border-border bg-surface text-muted-foreground',
                        )}
                      >
                        <span
                          aria-hidden
                          className={cn(
                            'inline-block h-2 w-2 rounded-full',
                            row.is_auto_create ? 'bg-success' : 'bg-muted-foreground/40',
                          )}
                        />
                        {row.is_auto_create ? 'auto-create on' : 'silent'}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <Select
                        value={row.default_priority}
                        onValueChange={(v) => setPriority(row.source, v as CrmTicketPriority)}
                      >
                        <SelectTrigger className="h-8 w-[140px] text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {priorityOptions.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
        </Card>
      )}

      <p className="mt-3 flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <Slash className="h-3 w-3" /> Tickets opened here run through the routing matrix the same
        way SLA breaches do. <Badge tone="info">tip</Badge> add a rule with{' '}
        <code>match_types=[cancellation]</code> for cancel ops, and{' '}
        <code>match_types=[refund]</code> with source <code>auto</code> so Accounts is paged when
        a prepaid order is cancelled.
      </p>
    </>
  );
}

